<template>
  <!-- 悬浮气泡 -->
  <div class="chat-bubble" v-if="!showPanel" @click="openPanel">
    <van-icon name="chat-o" size="24" color="#fff" />
  </div>

  <!-- 聊天面板 -->
  <van-overlay :show="showPanel" @click="closePanel" z-index="2000" />
  <div class="chat-panel" :class="{ open: showPanel }">
    <!-- 头部 -->
    <div class="chat-header">
      <span class="chat-title">AI 助手</span>
      <van-tag type="warning" size="mini">查询模式</van-tag>
      <van-icon name="cross" size="20" @click="closePanel" style="cursor:pointer" />
    </div>

    <!-- 消息区 -->
    <div class="chat-messages" ref="msgBox">
      <div v-for="(msg, i) in messages" :key="i" :class="msg.role">
        <div class="bubble" v-html="msg.content"></div>
      </div>
      <div v-if="loading" class="assistant">
        <div class="bubble typing">思考中 {{ elapsed }}s<span class="dots">...</span></div>
      </div>
    </div>

    <!-- 快捷问题 -->
    <div class="quick-asks" v-if="messages.length === 0">
      <span class="label">试试问我：</span>
      <div class="chips">
        <span class="chip" @click="sendMsg('帮我看看我的工作概况')">📊 我的工作概况</span>
        <span class="chip" @click="sendMsg('我有多少位专家？剩余配额情况？')">👨‍⚕️ 专家配额情况</span>
        <span class="chip" @click="sendMsg('最近有哪些待审批的会议？')">📋 待审批会议</span>
      </div>
    </div>

    <!-- 输入区 -->
    <div class="chat-input">
      <input v-model="input" placeholder="输入问题..." @keyup.enter="sendMsg()" :disabled="loading" />
      <button @click="sendMsg()" :disabled="loading || !input.trim()">发送</button>
    </div>
  </div>
</template>

<script>
import { ref, nextTick, watch } from 'vue';
import axios from '@/api/axios';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({ breaks: true, linkify: true });

export default {
  name: 'ChatBubble',
  setup() {
    const showPanel = ref(false);
    const messages = ref([]);
    const input = ref('');
    const loading = ref(false);
    const msgBox = ref(null);
    let timer = null;
    const elapsed = ref(0);

    function startTimer() {
      elapsed.value = 0;
      timer = setInterval(() => { elapsed.value++; }, 1000);
    }
    function stopTimer() {
      clearInterval(timer);
      timer = null;
    }

    function openPanel() { showPanel.value = true; }
    function closePanel() { showPanel.value = false; }

    function scrollBottom() {
      nextTick(() => {
        if (msgBox.value) msgBox.value.scrollTop = msgBox.value.scrollHeight;
      });
    }

    function fmtReply(text) {
      if (!text) return '';
      return md.render(text);
    }

    async function sendMsg(preset) {
      const text = preset || input.value.trim();
      if (!text || loading.value) return;

      messages.value.push({ role: 'user', content: text });
      input.value = '';
      loading.value = true;
      startTimer();
      scrollBottom();

      try {
        const resp = await axios.post('/agent/chat', { message: text, mode: 'readonly' }, { timeout: 30000 });
        const reply = resp.data?.reply || '抱歉，处理失败。';
        messages.value.push({ role: 'assistant', content: fmtReply(reply) });
      } catch (e) {
        const errMsg = e.response?.data?.error || e.message || '未知错误';
        messages.value.push({ role: 'assistant', content: `抱歉，请求失败：${errMsg}` });
      } finally {
        stopTimer();
        loading.value = false;
        scrollBottom();
      }
    }

    return { showPanel, messages, input, loading, elapsed, msgBox, openPanel, closePanel, sendMsg };
  }
};
</script>

<style scoped>
/* ====== 悬浮气泡 ====== */
.chat-bubble {
  position: fixed;
  bottom: 80px;
  right: 18px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--primary-blue, #145294);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(20,82,148,0.35);
  cursor: pointer;
  z-index: 2000;
  transition: transform 0.2s;
}
.chat-bubble:hover { transform: scale(1.08); }

/* ====== 面板 ====== */
.chat-panel {
  position: fixed;
  left: 0; bottom: 0; right: 0;
  height: 70vh;
  background: #fff;
  border-radius: 16px 16px 0 0;
  z-index: 2001;
  display: flex;
  flex-direction: column;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  box-shadow: 0 -4px 24px rgba(0,0,0,0.12);
}
.chat-panel.open { transform: translateY(0); }

/* 头部 */
.chat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  border-bottom: 1px solid #f0f0f0;
}
.chat-title { font-size: 16px; font-weight: 600; flex: 1; text-align: left; }

/* 消息区 */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.user, .assistant { display: flex; }
.user { justify-content: flex-end; }
.assistant { justify-content: flex-start; }

.bubble {
  max-width: 82%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.55;
  text-align: left;
  word-break: break-word;
}
.user .bubble { background: var(--primary-blue, #145294); color: #fff; border-bottom-right-radius: 4px; }
.assistant .bubble { background: #f5f5f5; color: #333; border-bottom-left-radius: 4px; }
.typing { color: #999; font-style: italic; }
.dots { animation: blink 1.4s infinite; }
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }

/* 快捷问题 */
.quick-asks { padding: 12px 16px; text-align: left; border-top: 1px solid #f0f0f0; }
.quick-asks .label { font-size: 12px; color: #999; }
.chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.chip { font-size: 12px; padding: 6px 12px; border-radius: 14px; background: #f0f5ff; color: var(--primary-blue, #145294); cursor: pointer; }

/* 输入区 */
.chat-input {
  display: flex;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid #f0f0f0;
}
.chat-input input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #e5e5e5;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
}
.chat-input button {
  padding: 10px 18px;
  border: none;
  border-radius: 20px;
  background: var(--primary-blue, #145294);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}
.chat-input button:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
